import * as React from 'react'
import { orderBy } from 'lodash'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import { Crumbs } from 'components/BreadCrumbs'
import { Container } from 'components/Layout'
import { InfoSidebar } from 'components/InfoSidebar'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import Button from 'components/Button'
import EmptyMessageCard from 'components/Cards/EmptyMessageCard'
import { ThreadList } from 'features/Messages/ThreadList'
import { ThreadListItem } from 'features/Messages/ThreadListItem'
import { MessageLog } from 'features/Messages/MessageLog'
import { MessageCreate } from 'features/Messages/MessageCreate'
import { ContactInfo } from './ContactInfo'
import { ServiceInfo } from './ServiceInfo'
import { FlowInfo } from './FlowInfo'
import { dispatchToProps } from './MessageThread.container'

import cn from 'classnames'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as s from './MessageThread.scss'

import {
  useListMessageThreadQuery,
  useListMessageThreadsQuery,
  ListMessageThreadsDocument,
  ListMessageThreadDocument,
} from 'src/generated/graphql'

export interface IIMessageThreadProps {
  serviceId: string
  otherNumber: string
}

export const MessageThread = (props: IIMessageThreadProps) => {
  const { data, loading } = useListMessageThreadQuery({
    variables: {
      otherNumber: props.otherNumber,
      serviceId: props.serviceId,
    },
  })

  return (
    <div className={s.messages}>
      {loading && <LoadingOverlay transparent />}
      <Container className="md">
        {data && data.messageThread && (
          <MessageLog
            // onMount={ () => subscribeToMore({
            //   document: SUBSCRIBE_MESSAGE,
            //   updateQuery: (args) => {
            //     console.log(args)
            //   }
            // })}
            messages={data.messageThread.messages}
            otherNumber={props.otherNumber}
          />
        )}
      </Container>
    </div>
  )
}

export interface IIMessageThreadSidebarProps {
  serviceId: string
  otherNumber: string
  showFlowCreateModal: typeof dispatchToProps['showFlowCreateModal']
  showContactCreateModal: typeof dispatchToProps['showContactCreateModal']
}

export const MessageThreadSidebar = (props: IIMessageThreadSidebarProps) => {
  const { otherNumber, serviceId, showFlowCreateModal, showContactCreateModal } = props
  const { data } = useListMessageThreadQuery({
    variables: {
      otherNumber,
      serviceId,
    },
  })

  return (
    <div>
      {data && data.messageThread && (
        <ContactInfo
          phoneNumber={otherNumber}
          contact={data.messageThread.contact}
          showContactCreateModal={showContactCreateModal}
        />
      )}
      {data && data.messageThread && data.messageThread.service && (
        <ServiceInfo service={data.messageThread.service} />
      )}
      {data && data.messageThread && data.messageThread.service && (
        <FlowInfo
          showFlowCreateModal={showFlowCreateModal}
          flowId={data.messageThread.service.twilioSmsFlowId}
          service={data.messageThread.service}
        />
      )}
    </div>
  )
}

export interface IMessageThreadPageProps {
  routeParams: {
    serviceId?: string
    otherNumber?: string
  }
  showFlowCreateModal: typeof dispatchToProps['showFlowCreateModal']
  showThreadCreateModal: typeof dispatchToProps['showThreadCreateModal']
  showContactCreateModal: typeof dispatchToProps['showContactCreateModal']
}

export const THREADS_LIMIT = 500

export const MessageThreadPage = (props: IMessageThreadPageProps) => {
  const [state, setState] = React.useState({
    page: 1,
    fetchMoreLoading: false,
    noMoreResults: false,
  })

  const { page, fetchMoreLoading, noMoreResults } = state

  const {
    showThreadCreateModal,
    showContactCreateModal,
    showFlowCreateModal,
    routeParams: { serviceId, otherNumber },
  } = props

  const { data, loading, fetchMore } = useListMessageThreadsQuery({
    variables: {
      page: 1,
      limit: THREADS_LIMIT,
    },
  })

  const listMessageThreadsQuery = {
    query: ListMessageThreadsDocument,
    variables: {
      page: 1,
      limit: THREADS_LIMIT,
    },
  }

  const listMessageThreadQuery = {
    query: ListMessageThreadDocument,
    variables: {
      otherNumber: otherNumber,
      serviceId: serviceId,
    },
  }

  return (
    <Page layout={{ header: 'app' }} title="Message" className={sLay['layout-column']}>
      <PageHeader>
        <Crumbs title="SMS Conversations" className={sLay['hide-xs']}>
          <div className={sLay.flex} />
          <Button
            rect
            color="tertiary"
            onClick={() => showThreadCreateModal()}
            style={{ marginRight: '10px' }}
          >
            New Conversation
          </Button>
        </Crumbs>
      </PageHeader>
      <div className={cn(sLay['layout-row'], sLay.flex)} style={{ overflowX: 'auto' }}>
        <ThreadList>
          <div className={cn(sLay['hide-gt-xs'], sMarg.m10)}>
            <Button
              className={cn(sMarg.w100)}
              rect
              color="tertiary"
              onClick={() => showThreadCreateModal()}
            >
              New Conversation
            </Button>
          </div>

          {loading && <LoadingOverlay transparent />}
          {data &&
            data.messageThreads &&
            orderBy(data.messageThreads, ['lastMessage.dateSent'], ['desc']).map((thread, idx) => (
              <ThreadListItem
                key={idx}
                otherNumber={thread.otherNumber}
                serviceId={thread.serviceId}
                time={thread.lastMessage.dateSent}
                contact={thread.contact}
                message={thread.lastMessage.body}
              />
            ))}
          {!loading && (
            <div className={cn(sLay['layout-column'], sLay['layout-align-center-center'])}>
              <Button
                rect
                size="xs"
                color="tertiary"
                style={{ margin: '10px 0px' }}
                loading={fetchMoreLoading}
                disabled={noMoreResults}
                onClick={async () => {
                  setState({ ...state, page: state.page + 1, fetchMoreLoading: true })

                  return fetchMore({
                    variables: {
                      limit: THREADS_LIMIT,
                      page: page + 1,
                    },
                    updateQuery: (prev, { fetchMoreResult }) => {
                      if (!fetchMoreResult) return prev
                      if (fetchMoreResult.messageThreads.length === 0) {
                        setState({ ...state, noMoreResults: true })
                      }
                      return {
                        ...prev,
                        messageThreads: [...prev.messageThreads, ...fetchMoreResult.messageThreads],
                      }
                    },
                  })
                    .then(() => setState({ ...state, fetchMoreLoading: false }))
                    .catch(() => setState({ ...state, fetchMoreLoading: false }))
                }}
              >
                Load More
              </Button>
            </div>
          )}
        </ThreadList>
        <div
          className={cn(sLay['layout-column'], sLay.flex, s.messagesArea)}
          style={{ position: 'relative' }}
        >
          {serviceId && otherNumber ? (
            <MessageThread serviceId={serviceId} otherNumber={otherNumber} />
          ) : (
            <Container
              className={cn(
                sLay['layout-column'],
                sLay['layout-align-center-center'],
                sLay['flex'],
                'md'
              )}
            >
              <EmptyMessageCard
                title="No conversation selected"
                subtitle="Select a conversion in the sidebar to the left or click 'New Conversation'"
              />
            </Container>
          )}
          <Container className="md">
            {serviceId && otherNumber && (
              <MessageCreate
                serviceId={serviceId}
                to={otherNumber}
                listMessageThreadsQuery={listMessageThreadsQuery}
                listMessageThreadQuery={listMessageThreadQuery}
              />
            )}
          </Container>
        </div>
        <InfoSidebar>
          {serviceId && otherNumber && (
            <MessageThreadSidebar
              serviceId={serviceId}
              otherNumber={otherNumber}
              showFlowCreateModal={showFlowCreateModal}
              showContactCreateModal={showContactCreateModal}
            />
          )}
        </InfoSidebar>
      </div>
    </Page>
  )
}



// WEBPACK FOOTER //
// src/pages/MessageThread/MessageThread.tsx